import React, { FC, ReactNode } from 'react';
import { BasicButton, GhostButton } from '../../../../../widgets/buttons';
import { Icon } from '../../../../../widgets/icon';
import { ChevronIcon, SelectedIcon } from '../../../../../widgets/icons/scene';
import { DropdownActionItem, DropdownActions } from '../../../../../widgets/dropdown';
import { stringFormat, T } from 'src/shared/helpers';
import { INSPECTOR_BACKGROUND, INSPECTOR_MARK_GLYPH, INSPECTOR_MARK_LINE, INSPECTOR_MARK_RADIUS, INSPECTOR_MARK_SIZE } from '../treeRowLook';
import { Input, InputProps, SearchInput } from '../../../../../widgets/input';
import { LEFT_BY_KEYBOARD, leaveInlineField, rearmInlineField } from '../inlineNameField';
import { thinScrollbar } from '../../../../../widgets/scrollbar';
import { ZTabs } from '../../../../../widgets/ztabs';
import { FormControl } from '../../../../../widgets/formControl';
import { PropertiesPanelAccordion } from '../scene-properties-panel/propertiesPanelAccordion';
import { PropertiesPanelLookProvider } from '../scene-properties-panel/propertiesPanelLook';
import { PropertiesPanelSectionScope } from '../scene-properties-panel/propertiesPanelSectionMemory';
import styled, { css } from 'styled-components';

export const ComposerPanel = styled.section`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    color: var(--composer-fg, #263238);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dbe2e6);
    border-radius: 16px;
    box-shadow: var(--composer-shadow, rgba(38, 50, 56, 0.04) 0 1px 2px);
`;

export const ComposerPanelHeader = styled.header`
    display: flex;
    min-height: 38px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerPanelTitle = styled.div`
    min-width: 0;
`;

export const ComposerEyebrow = styled.div`
    overflow: hidden;
    color: var(--composer-faint, #677078);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 16px;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const ComposerTitle = styled.div`
    overflow: hidden;
    color: var(--composer-fg, #1e2a38);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ComposerToolbar = styled.div`
    display: flex;
    min-height: 44px;
    flex: 0 0 auto;
    align-items: center;
    flex-wrap: wrap;
    gap: 3px;
    padding: 6px 8px;
    background: var(--composer-panel, #fff);
    border-bottom: 1px solid var(--composer-line, #dbe2e6);
`;

/* The Scene Editor add button: 32px tall, a 12px label and a glyph a shade larger than the text. */
export const ComposerAddButton = styled(BasicButton)`
    min-width: 0;
    height: 32px;
    padding: 0 9px;
    border-radius: 6px;
    gap: 4px;
    font-size: 12px;

    > svg {
        width: 17px;
        height: 17px;
    }
`;

export const ComposerToolbarSpacer = styled.span`
    flex: 1;
`;

/* Only the pressed state is the Composer's own: the resting colour and the hover are GhostButton's. */
export const ComposerIconButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })<{ $active?: boolean; $danger?: boolean }>`
    width: 30px;
    min-width: 30px;
    height: 32px;
    min-height: 32px;
    border-radius: 6px;

    ${(props) => props.$danger && `color: ${props.theme.app.component.textButton.destructive.default.color};`}

    ${(props) =>
        props.$active &&
        css`
            color: var(--composer-accent-text, #a8480b);
            background: var(--composer-accent-wash, #fdf0e6);
        `}

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
    }
`;

/* Every row command slot is the shared ghost button at row density: the resting colour, the hover
   and the danger colour are the component's own, and only the pressed wash is the Composer's. */
export const ComposerRowActionButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })<{
    $active?: boolean;
    $danger?: boolean;
    $off?: boolean;
}>`
    width: 28px;
    min-width: 28px;
    height: 28px;
    min-height: 28px;
    border-radius: 5px;

    ${(props) =>
        props.$danger &&
        css`
            color: ${props.theme.app.component.textButton.destructive.default.color};

            &:hover:not([disabled]),
            &:active:not([disabled]) {
                color: ${props.theme.app.component.textButton.destructive.hover.color};
            }
        `}

    ${(props) =>
        (props.$active || props.$off) &&
        css`
            color: var(--composer-accent-text, #a8480b);

            &:hover:not([disabled]),
            &:active:not([disabled]) {
                color: var(--composer-accent-text, #a8480b);
            }
        `}

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

/* SearchInput carries the magnifier and hands this class to the field it wraps, not to its own
   container: the container is already a full-width block, so the row needs no flex rules of its own. */
export const ComposerSearch = styled(SearchInput)`
    height: 32px;
`;

export const ComposerSearchRow = styled.div`
    flex: 0 0 auto;
    padding: 8px 10px;
    border-bottom: 1px solid var(--composer-line-soft, #eef1f3);
`;

// LA NOTA SUI GESTI STA FERMA IN FONDO, sotto una linea (round 4, decisione 4 della scena): la stessa
// del pannello di una lista nello Scene Editor. In coda all'elenco scorreva via con le righe, e con un
// elenco lungo non si vedeva proprio quando serve.
export const ComposerSelectionFooter = styled.p`
    display: flex;
    flex: none;
    align-items: center;
    min-height: 36px;
    margin: 0;
    padding: 6px 12px;
    border-top: 1px solid var(--composer-line-soft, #eef1f3);
    color: var(--composer-faint, #677078);
    font-size: 11px;
    line-height: 16px;
`;

// LA CARD TRATTEGGIATA IN FONDO A UN ELENCO DI CARD (round 4, pacchetto I) — azioni, espressioni dei
// Links: il posto della prossima, dove l'occhio arriva dopo aver letto l'ultima.
export const ComposerAddCard = styled.button`
    display: flex;
    width: 100%;
    min-height: 40px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 4px;
    padding: 0 10px;
    border: 1px dashed var(--composer-line, #dce3eb);
    border-radius: 8px;
    color: var(--composer-faint, #677078);
    background: transparent;
    font: inherit;
    font-size: 12px;
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }

    &:hover:not(:disabled),
    &:focus-visible {
        color: var(--composer-fg, #1e2a38);
        border-color: var(--composer-accent, #e8650a);
        outline: none;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export const ComposerScrollBody = styled.div`
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 8px 6px;
    ${thinScrollbar}
`;

// IL CORPO DI UNA SELEZIONE MULTIPLA MISURA QUELLO DELLO SCENE EDITOR (utente, 2026-10-15; «Body» di
// inspectorShell): dodici pixel a sinistra e la corsia della barra sempre riservata a destra. Con gli
// otto e sei del corpo comune i comandi arrivavano quasi al bordo, e i due pannelli non si somigliavano.
export const ComposerSelectionBody = styled(ComposerScrollBody)`
    padding: 12px 0 20px 12px;
    scrollbar-gutter: stable;
`;

export const ComposerList = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
`;

export const ComposerSubjectHeader = styled.div`
    display: grid;
    min-height: 56px;
    flex: 0 0 auto;
    /* La terza colonna è dei comandi della testata («Show in the list»), in alto a destra. */
    grid-template-columns: ${INSPECTOR_MARK_SIZE}px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-bottom: 1px solid var(--composer-line, #dbe2e6);
`;

// LA MARCA È QUELLA DEL PROTOTIPO (`.ident-icon`; utente, 2026-10-15): ventisei pixel, grigia come la
// colonna dell'Inspector e staccata da un filo — la stessa della testata dello Scene Editor (treeRowLook).
export const ComposerSubjectMark = styled.div<{ $kind?: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: flex;
    width: ${INSPECTOR_MARK_SIZE}px;
    height: ${INSPECTOR_MARK_SIZE}px;
    flex: none;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #5c6b7a)';
    }};
    background: ${INSPECTOR_BACKGROUND};
    border-radius: ${INSPECTOR_MARK_RADIUS};
    box-shadow: inset 0 0 0 1px ${INSPECTOR_MARK_LINE};

    ${Icon},
    > svg {
        width: ${INSPECTOR_MARK_GLYPH}px;
        height: ${INSPECTOR_MARK_GLYPH}px;
    }

    /* Una fotografia riempie il riquadro, il glifo no. */
    > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    /* Same as the tree row icons: the glyph takes the kind colour set above. */
    svg {
        fill: currentColor;
    }
`;

export const ComposerSubjectContent = styled.div`
    min-width: 0;
`;

export const ComposerSubjectName = styled.div`
    overflow: hidden;
    color: var(--composer-fg, #263238);
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/* The inspector name field: plain text until it is pointed at, the way the Scene Editor writes names. */
const SubjectNameField = styled(Input)`
    width: 100%;
    /* No declared height: the field is as tall as its own text plus padding, exactly like the
       inspector name field, so the two read as one control. */
    height: auto;
    padding: 3px 6px;
    margin-left: -6px;
    color: var(--composer-fg, #263238);
    background: transparent;
    border: 1px solid transparent;
    border-radius: 5px;
    box-shadow: none;
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;

    /* An invalid name (empty, or the same as another attribute) speaks the warning family the tree
       triangle uses. The shared Input painted a red ring that outlived the focus and read as a
       field still being edited. */
    ${(props) =>
        props.isError &&
        css`
            background: var(--composer-warn-surface, #fff8ed);
            border-color: var(--composer-warn-line-strong, #f2c98d);
        `}

    &:hover:not(:focus):not(:disabled):not(${LEFT_BY_KEYBOARD}) {
        background: ${(props) => (props.isError ? 'var(--composer-warn-wash, #fff0df)' : 'var(--composer-hover, #eceff1)')};
        border-color: ${(props) => (props.isError ? 'var(--composer-warn-line-strong, #f2c98d)' : 'var(--composer-line, #dbe2e6)')};
    }

    &:focus {
        background: var(--composer-hover, #eceff1);
        border-color: var(--composer-accent, #e8650a);
        box-shadow: none;
    }
`;

/* Enter confirms and leaves the field as a click outside would, like the inspector name in the Scene
   Editor: the change is already written on every keystroke, so leaving is the whole of "confirm". */
export const ComposerSubjectNameInput = React.forwardRef<HTMLInputElement, InputProps>(({ onKeyDown, onFocus, onMouseLeave, ...props }, ref) => (
    <SubjectNameField
        ref={ref}
        {...props}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(event);
            if (!event.defaultPrevented && event.key === 'Enter') leaveInlineField(event.currentTarget);
        }}
        onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
            rearmInlineField(event.currentTarget);
            onFocus?.(event);
        }}
        onMouseLeave={(event: React.MouseEvent<HTMLInputElement>) => {
            rearmInlineField(event.currentTarget);
            onMouseLeave?.(event);
        }}
    />
));
ComposerSubjectNameInput.displayName = 'ComposerSubjectNameInput';

export const ComposerSubjectMeta = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    color: var(--composer-faint, #78909c);
    font-size: 9px;
`;

export type ComposerKind = 'group' | 'attribute' | 'option' | 'neutral';

// L'INCHIOSTRO E IL LAVATO DI OGNI SPECIE, in un posto solo: li leggono la pastiglia della specie e il
// badge «N selected» di una selezione multipla.
const KIND_INK: Record<ComposerKind, string> = {
    group: 'var(--composer-kind-group, #7040d0)',
    attribute: 'var(--composer-kind-attribute, #0e7c7c)',
    option: 'var(--composer-kind-option, #b32d7d)',
    neutral: 'var(--composer-muted, #455a64)'
};
const KIND_TINT: Record<ComposerKind, string> = {
    group: '#dfcff9',
    attribute: '#c4e4e4',
    option: '#f4cadf',
    neutral: 'var(--composer-sunken, #f9f9f9)'
};

/** L'inchiostro e il lavato di una specie, per chi li dipinge fuori da un badge (le voci del menu Add). */
export const composerKindTone = (kind: ComposerKind) => ({ ink: KIND_INK[kind], wash: KIND_TINT[kind] });

/** IL VIOLA DELL'AI, quello che la galleria Add di prima dava a «Generate with AI». */
export const COMPOSER_AI_TONE = { ink: 'var(--composer-ai, #6b36ce)', wash: 'var(--composer-ai-wash, #f7f3fe)' };

/** La specie di un mucchio: quella di tutte le righe, se è una sola; altrimenti nessuna. */
export const composerPileKind = (kinds: ComposerKind[]): ComposerKind => (kinds.length > 0 && kinds.every((kind) => kind === kinds[0]) ? kinds[0] : 'neutral');

const SelectionBadgeButton = styled.button<{ $kind: ComposerKind }>`
    display: inline-flex;
    height: 15px;
    align-items: center;
    gap: 2px;
    padding: 0 4px 0 6px;
    border: 0;
    border-radius: 999px;
    /* LO STESSO BADGE DELLO SCENE EDITOR (utente, 2026-10-15; «Tag» di inspectorShell): contorno e parola
       nel colore della specie che conta — rosa per le opzioni e le aree, verde-acqua per gli attributi,
       viola per i gruppi —, il lavato solo passandoci sopra. Un mucchio misto resta grigio. */
    color: ${(props) => KIND_INK[props.$kind]};
    background: transparent;
    box-shadow: inset 0 0 0 1px ${(props) => KIND_INK[props.$kind]};
    font: inherit;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        background: ${(props) => KIND_TINT[props.$kind]};
        outline: none;
    }

    svg {
        width: 10px;
        height: 10px;
        fill: currentColor;
    }
`;

/**
 * LA TESTATA DI UNA SELEZIONE MULTIPLA (round 4, pacchetto F), la stessa dello Scene Editor: «Multiple
 * elements» e un badge «N selected» che apre l'elenco; un clic su una voce sceglie quella sola.
 */
export const ComposerSelectionHeader: FC<{
    entries: { id: string; name: string; icon?: React.ReactNode }[];
    onPick: (id: string) => void;
    /** La specie delle righe scelte: dà il colore al badge. */
    kind: ComposerKind;
}> = ({ entries, onPick, kind }) => (
    <ComposerSubjectHeader>
        <ComposerSubjectMark $kind='neutral' aria-hidden='true'>
            <SelectedIcon />
        </ComposerSubjectMark>
        <ComposerSubjectContent>
            <ComposerSubjectName>{T._('Multiple elements', 'Admin')}</ComposerSubjectName>
            <ComposerSubjectMeta>
                <DropdownActions
                    header={
                        <SelectionBadgeButton type='button' $kind={kind}>
                            {stringFormat(T._('{0} selected', 'Admin'), entries.length.toString())}
                            <ChevronIcon />
                        </SelectionBadgeButton>
                    }
                    items={entries.map((entry) => (
                        <DropdownActionItem key={entry.id} icon={entry.icon} onClick={() => onPick(entry.id)}>
                            {entry.name}
                        </DropdownActionItem>
                    ))}
                    orientation='LEFT'
                />
            </ComposerSubjectMeta>
        </ComposerSubjectContent>
    </ComposerSubjectHeader>
);

export const ComposerKindBadge = styled.span<{ $kind?: ComposerKind }>`
    display: inline-flex;
    height: 15px;
    align-items: center;
    padding: 0 6px;
    color: ${(props) => KIND_INK[props.$kind ?? 'neutral']};
    /* A filled tint, like the inspector badges: the kind is read by colour, not by an outline. */
    background: ${(props) => KIND_TINT[props.$kind ?? 'neutral']};
    border-radius: 999px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: uppercase;
`;

const ComposerTabsRoot = styled(ZTabs)`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1;
    flex-direction: column;

    .react-tabs__tab-list {
        width: 100%;
        min-height: 38px;
        margin: 0;
        padding: 6px 8px;
        overflow-x: auto;
        /* SCORRE DI LATO, SENZA BARRA (prototipo, decisione 66): con una lingua più lunga le tab non
           stanno nella colonna, e una sfumatura dice da che parte ce ne sono altre. */
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }

        &[data-fade='left'] {
            mask-image: linear-gradient(to right, transparent, #000 28px);
        }

        &[data-fade='right'] {
            mask-image: linear-gradient(to left, transparent, #000 28px);
        }

        &[data-fade='both'] {
            mask-image: linear-gradient(to right, transparent, #000 28px, #000 calc(100% - 28px), transparent);
        }
        background: var(--composer-panel, #fff);
        border-bottom: 1px solid var(--composer-line, #dbe2e6);
        border-radius: 0;
    }

    .react-tabs__tab {
        flex: 0 0 auto;
        height: 26px;
        /* Sei pixel e non otto: sei tab con tre contatori stanno nei 480 dell'Inspector (2026-10-15). */
        padding: 0 6px;
        color: var(--composer-muted, #455a64);
        background: transparent;
        border-radius: 6px;
        box-shadow: none;
        font-size: 12px;
        font-weight: 500;
    }

    .react-tabs__tab:hover {
        color: var(--composer-fg, #1e2a38);
        background: var(--composer-hover, #e9eff5);
    }

    .react-tabs__tab--selected,
    .react-tabs__tab--selected:hover {
        color: var(--composer-accent-text, #a8480b);
        background: rgb(0 0 0 / 6%);
        box-shadow: none;
    }

    .react-tabs__tab-panel {
        min-width: 0;
        min-height: 0;
        flex: 1;
        overflow: hidden;
    }

    .react-tabs__tab-panel--selected {
        display: flex;
        flex-direction: column;
    }
`;

/**
 * LE TAB DEL COMPOSER E DELL'INSPECTOR DELLA SCENA. Quando non stanno nella colonna la fila scorre di
 * lato — con la rotella o trascinando — e sfuma dal lato dove ce ne sono altre; la tab scelta resta in
 * vista (prototipo, decisione 66). Nessun involucro in più: la fila si trova da un attributo sul
 * contenitore delle tab, così chi le stila (`:first-child` nell'Inspector della scena) le trova dov'erano.
 */
export const ComposerTabs: FC<React.ComponentProps<typeof ComposerTabsRoot>> = (props) => {
    const stripId = React.useId();

    React.useEffect(() => {
        const host = document.querySelector<HTMLElement>(`[data-tab-strip="${CSS.escape(stripId)}"]`);
        const list = host?.querySelector<HTMLElement>(':scope > .react-tabs__tab-list');
        if (!list) return;
        const syncFade = () => {
            const left = list.scrollLeft > 1;
            const right = list.scrollLeft + list.clientWidth < list.scrollWidth - 1;
            const fade = left && right ? 'both' : left ? 'left' : right ? 'right' : '';
            if (fade) list.setAttribute('data-fade', fade);
            else list.removeAttribute('data-fade');
        };
        const revealSelected = () => {
            const tab = list.querySelector<HTMLElement>('[aria-selected="true"]');
            if (!tab) return;
            if (tab.offsetLeft < list.scrollLeft) list.scrollLeft = tab.offsetLeft - 8;
            else if (tab.offsetLeft + tab.offsetWidth > list.scrollLeft + list.clientWidth)
                list.scrollLeft = tab.offsetLeft + tab.offsetWidth - list.clientWidth + 8;
        };
        // La rotella verticale sposta la fila di lato: senza barra è il gesto che resta a chi non ha un trackpad.
        const onWheel = (event: WheelEvent) => {
            if (list.scrollWidth <= list.clientWidth || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
            list.scrollLeft += event.deltaY;
            event.preventDefault();
        };
        const resize = new ResizeObserver(syncFade);
        const selection = new MutationObserver(() => {
            revealSelected();
            syncFade();
        });
        resize.observe(list);
        selection.observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-selected'], childList: true });
        list.addEventListener('scroll', syncFade, { passive: true });
        list.addEventListener('wheel', onWheel, { passive: false });
        revealSelected();
        syncFade();
        return () => {
            resize.disconnect();
            selection.disconnect();
            list.removeEventListener('scroll', syncFade);
            list.removeEventListener('wheel', onWheel);
        };
    }, [stripId]);

    return <ComposerTabsRoot {...props} data-tab-strip={stripId} />;
};

export const ComposerTabCount = styled.span`
    display: inline-flex;
    min-width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    color: var(--composer-faint, #677078);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 999px;
    font-size: 10px;
`;

/* An answer, not a blank panel: the glyph says what the column is for and the line says what to do
   next. Same shape as the Scene Editor inspector, so the two stanze rispondono allo stesso modo. */
const EmptyStateBox = styled.div`
    display: flex;
    height: 100%;
    min-height: 180px;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px 28px;
    color: var(--composer-muted, #455a64);
    text-align: center;

    p {
        max-width: 34ch;
        margin: 0;
        font-size: 13px;
        line-height: 1.6;
    }

    svg {
        width: 26px;
        height: 26px;
        opacity: 0.5;
    }
`;

export const ComposerEmptyState: FC<{ children?: ReactNode; icon?: ReactNode }> = ({ children, icon }) => (
    <EmptyStateBox>
        {icon ?? <SelectedIcon aria-hidden='true' />}
        <p>{children}</p>
    </EmptyStateBox>
);

export const ComposerDetailsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

// IL PASSO FRA LE SEZIONI È QUELLO DELLE CARD DELLA SCENA: dieci pixel. Scritto come margine e non come
// gap, perché la card della scena porta già il suo `margin-top` fra due card vicine e i due si sommavano.
export const ComposerFormStack = styled.div`
    display: flex;
    flex-direction: column;

    > * + * {
        margin-top: 10px;
    }
`;

export const ComposerDetailCard = styled.div`
    padding: 12px;
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
`;

export const ComposerDetailLabel = styled.div`
    margin-bottom: 4px;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
`;

export const ComposerDetailValue = styled.div`
    overflow-wrap: anywhere;
    color: var(--composer-fg, #1e2a38);
    font-size: 13px;
    line-height: 19px;
`;

// L'INSPECTOR È BIANCO COME L'ALBERO (utente, 2026-10-15), come nello Scene Editor: le sezioni bianche
// si staccano col loro bordo, non con un fondo grigio dietro che faceva della colonna un'altra cosa.
// Il corpo non ha un fondo suo: prende quello della colonna, grigia nell'Inspector (composerInspectorTone).
export const ComposerSettingsBody = styled(ComposerScrollBody)`
    padding: 10px;
    background: transparent;
`;

/* The Composer forms keep the shared Input, TextArea and AdvancedSelect at their own size: only
   the label above them is written smaller here, the way the inspector labels its rows. */
export const composerFieldDensity = css`
    label {
        margin-bottom: 4px;
        color: var(--composer-fg, #263238);
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
    }

    ${() => composerSubsectionLabel}
`;

// I NOMI DELLE PROPRIETÀ SONO IN GRASSETTO (utente, 2026-10-15): «Code», «Description», «Group image» si
// leggono come i titoli delle sotto-card dello Scene Editor, nell'inchiostro pieno. Il nome sopra un
// controllo a tutta riga — uno slot, una scelta a segmenti, che lo dicono con `data-composer-wide` — fa da
// titolo della sua sottosezione (prototipo V2, «DIRECTION», «CAMERA POSITION»): sta sopra, e il controllo
// prende la riga intera.
export const composerSubsectionLabel = css`
    ${FormControl}:has([data-composer-wide]) > label {
        margin: 0 0 6px;
    }
`;

/* The section title stands above its card, the way the inspector writes SCENE or SHADING over
   the cards that follow it. */
export const ComposerFormGroup = styled.section`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const ComposerFormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dbe2e6);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    ${composerFieldDensity}
`;

export const ComposerFormSectionTitle = styled.h3`
    margin: 0;
    padding: 0 2px;
    color: var(--composer-muted, #455a64);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

// LA RIGA DI UN CAMPO, COME NELL'INSPECTOR DELLA SCENA (utente, 2026-10-15): il nome a sinistra in una
// colonna sua, il controllo a destra, compatto (28 pixel come i campi della scena). La regola sta sul
// corpo della sezione e non su ogni campo, così vale anche per i campi annidati — un prezzo, un passo di
// un editor — senza che ognuno lo dichiari.
//
// RESTANO IN COLONNA — nome sopra e controllo a tutta larghezza — i campi che non stanno accanto a un
// nome: un testo lungo, un caricamento d'immagine, un campo con la sua didascalia sotto il nome. Lì la
// colonna stretta del controllo li schiaccerebbe.
const COMPOSER_LABEL_COLUMN = '104px';

const ComposerSectionFields = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 10px;

    ${FormControl} > label {
        margin: 0 0 4px;
        color: var(--composer-fg, #263238);
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        white-space: normal;
    }

    ${FormControl}:has(> label):not(:has(> label + div + div)):not(:has(textarea)):not(:has(input[type='file'])):not(:has([data-composer-wide])) {
        display: grid;
        grid-template-columns: ${COMPOSER_LABEL_COLUMN} minmax(0, 1fr);
        column-gap: 10px;
        align-items: center;

        > label {
            margin: 0;
        }

        > div {
            min-width: 0;
        }
    }

    ${composerSubsectionLabel}

    /* Un interruttore sta in fondo alla riga, come nella scena: a sinistra il nome, a destra il sì/no. */
    ${FormControl}:has([role='switch']) > div {
        display: flex;
        justify-content: flex-end;
    }

    ${Input} {
        height: 28px;
        padding: 3px 8px;
        font-size: 12px;
    }

    /* Il controllo della tendina condivisa (react-select): una misura sotto, come compactSelectStyles. */
    [class$='-control'] {
        min-height: 28px;
        height: auto;
        font-size: 12px;
    }
`;

/**
 * UNA SEZIONE DELL'INSPECTOR DEL COMPOSER È UNA CARD DELLA SCENA (utente, 2026-10-15): titolo dentro la
 * card con la freccia per chiuderla, i campi col nome a sinistra. Aperta di partenza, tutte: chi apre
 * un attributo deve vedere tutto quello che può cambiare, e chiudere è una scelta sua.
 * Prima il titolo stava sopra la card, fuori, e la sezione non si chiudeva.
 */
export const ComposerSection: FC<{
    /** Il nome stabile con cui la sezione si ricorda chiusa: «area.size», «option.general». */
    id: string;
    title: ReactNode;
    trailing?: ReactNode;
    /** Aperta comunque mentre dentro c'è un errore da correggere. */
    forceOpen?: boolean;
    children: ReactNode;
}> = ({ id, title, trailing, forceOpen, children }) => (
    <PropertiesPanelLookProvider value='scene'>
        <PropertiesPanelSectionScope value='composer'>
            <PropertiesPanelAccordion memoryId={id} header={title} trailing={trailing} forceOpen={forceOpen} defaultIsOpen>
                <ComposerSectionFields>{children}</ComposerSectionFields>
            </PropertiesPanelAccordion>
        </PropertiesPanelSectionScope>
    </PropertiesPanelLookProvider>
);

// LA SPIEGAZIONE DI UN GRUPPO DI CAMPI STA SOTTO DI LORO, non sotto il nome del primo: lì spostava il
// campo sotto il testo e lo staccava dai vicini (la larghezza di un'area, sotto e fuori riga rispetto all'altezza).
// LE SCORCIATOIE DI UN ATTRIBUTO VUOTO: da dove partire, in un riquadro tratteggiato sopra le tab — tre opzioni
// già nominate per un attributo a opzioni, un'area di testo, d'immagine o di tutt'e due per uno di aree.
export const ComposerPresetBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 10px 12px 0;
    padding: 10px 12px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f9f9f9);
    border: 1px dashed var(--composer-line, #dce3eb);
    border-radius: 8px;
    font-size: 12px;
`;

export const ComposerPresetButtons = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

/** Una lista vuota: il posto tratteggiato della prima voce, e sotto che cosa vuol dire. */
export const ComposerEmptyList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ComposerFieldNote = styled.p`
    margin: 0;
    color: var(--composer-muted, #5c6b7a);
    font-size: 11px;
    line-height: 16px;
`;

export const ComposerUnsupportedNote = styled.div`
    padding: 10px 12px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    font-size: 12px;
    line-height: 18px;
`;

// Like the Scene Editor seams: nothing at rest, a short accent pill once the pointer is on it.
// LA PRESA È QUELLA DELLO SCENE EDITOR (utente, 2026-10-15; `Resizer` in workspaceLayout): larga nove
// pixel e a cavallo della cucitura di sei — sporge di un pixel e mezzo per parte —, ferma la si vede
// appena; al passaggio compaiono la linea da un pixel e la linguetta arancio che dice «si tira».
export const ComposerResizeHandle = styled.div`
    position: relative;
    z-index: 5;
    width: 9px;
    min-width: 9px;
    height: 100%;
    margin: 0 -1.5px;
    color: var(--composer-line, #dbe2e6);
    background: transparent;
    cursor: col-resize;
    touch-action: none;

    &::before {
        position: absolute;
        top: 50%;
        left: 4px;
        width: 1px;
        height: 34px;
        background: currentColor;
        opacity: 0;
        transform: translateY(-50%);
        transition: opacity 120ms ease;
        content: '';
    }

    &::after {
        position: absolute;
        top: 50%;
        left: 2px;
        width: 5px;
        height: 34px;
        background: var(--composer-accent, #e8650a);
        border-radius: 999px;
        opacity: 0;
        transform: translateY(-50%);
        transition: opacity 130ms ease;
        pointer-events: none;
        content: '';
    }

    &:hover,
    &:active,
    &:focus-visible {
        color: var(--composer-accent, #e8650a);
        outline: none;
    }

    &:hover::before,
    &:active::before,
    &:focus-visible::before,
    &:hover::after,
    &:active::after,
    &:focus-visible::after {
        opacity: 1;
    }
`;

/**
 * LA COLONNA DELL'INSPECTOR È GRIGIA (utente, 2026-10-15; prototipo `.inspector`): il pannello, la
 * testata del soggetto, la barra dei comandi, la fila delle tab e il corpo. Bianche restano le card,
 * le sezioni e i campi, col loro bordo. Lo applica chi impagina la colonna, su qualunque pannello ci
 * finisca — opzione, area, attributo, gruppo, mucchio, preset —, così nessuno dei pannelli deve saperlo;
 * l'albero, che usa gli stessi pezzi, resta bianco.
 */
export const composerInspectorTone = css`
    background: ${INSPECTOR_BACKGROUND};

    ${ComposerToolbar}, .react-tabs__tab-list {
        background: transparent;
    }

    /* Una nota grigia sul grigio della colonna sparirebbe: qui è una card come le altre. */
    ${ComposerUnsupportedNote} {
        background: var(--composer-panel, #fff);
    }
`;
